import { UserSearch } from 'iconsax-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@src/design-system/command';
import { Checkbox } from '@src/design-system/checkbox';
import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import { useTiclickTableContext } from '@src/design-system/molecules/ticlick-table/context/ticlick-table-context-hook';
import { StudentAssigneeInfo } from '@features/participants/components/modals/add-new-participant/components/student-assignee-info';
import type { TSubmission } from '@shared/types/submission';
import type { TStudent } from '@shared/types/student';

type TParticipantRow = TSubmission | TStudent;

const isSubmissionRow = (row: TParticipantRow): row is TSubmission =>
  'submitted_by' in row;

function getMemberFromRow(row: TParticipantRow) {
  return isSubmissionRow(row) ? row.submitted_by : row.user;
}

function getMemberIdFromRow(row: TParticipantRow): number | undefined {
  return getMemberFromRow(row)?.id;
}

export function SelectedParticipantsPopover() {
  const { table } = useTiclickTableContext<TParticipantRow>();
  const selectedRows = table
    .getSelectedRowModel()
    .rows.flat()
    .map((row) => row.original as TParticipantRow);

  const handleDeselect = (memberId: number) => {
    const row = table
      .getRowModel()
      .rows.find((item) => getMemberIdFromRow(item.original as TParticipantRow) === memberId);
    row?.toggleSelected(false);
  };

  return (
    <Popover modal>
      <PopoverTrigger
        className={cn(
          inputVariants({ variant: 'fill', size: 'lg' }),
          'flex w-full cursor-pointer items-center justify-between text-sm'
        )}
      >
        <span className='flex items-center gap-2'>
          <UserSearch
            size={20}
            color='#584777'
          />
          Show Selected Participants ({selectedRows.length})
        </span>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='w-[var(--radix-popover-trigger-width)] p-0'
      >
        <Command>
          <CommandInput
            containerClassName='rounded-none mb-0 border-0 border-b-1 border-pale-border'
            placeholder='Search for participants'
          />
          <CommandList className='max-h-[min(240px,var(--radix-popover-content-available-height,240px))]'>
            <CommandEmpty>No participants found</CommandEmpty>
            <CommandGroup className='p-0'>
              {selectedRows.map((row) => {
                const member = getMemberFromRow(row);
                if (!member) {
                  return null;
                }

                return (
                  <CommandItem
                    key={member.id}
                    value={`${member.first_name} ${member.last_name} ${member.email}`}
                    className='py-2 block overflow-hidden bg-white last:mb-0 border-b border-primary-200 last:border-b-0 rounded-none'
                    data-selected={false}
                    onSelect={() => handleDeselect(member.id)}
                  >
                    <div className='flex items-center gap-2'>
                      <Checkbox
                        checked
                        onCheckedChange={(checked) => {
                          if (!checked) {
                            handleDeselect(member.id);
                          }
                        }}
                        onClick={(event) => event.stopPropagation()}
                      />
                      <StudentAssigneeInfo {...member} />
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { getMemberIdFromRow };
